import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { useSiteContent } from "@/context/ContentContext";
import { TRUST_POINTS, CONTACT } from "@/data/content";
import { trackCall } from "@/lib/analytics";

export default function Hero() {
  const { hero: HERO } = useSiteContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden bg-[#0b1120]">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 will-change-transform">
        <img src={HERO.image} alt="Luxury marble bathroom by Perth Tiling WA" fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
      </motion.div>
      {/* Bold dark wash for contrast */}
      <div className="absolute inset-0 bg-[#0b1120]/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0b1120] via-transparent to-[#0b1120]/60" />
      {/* Gold spotlight */}
      <div className="absolute -top-40 right-0 h-[500px] w-[500px] rounded-full bg-[#9c7a4d]/15 blur-[120px] pointer-events-none" aria-hidden />

      <div className="relative z-10 min-h-[100svh] mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-20 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mb-6"
        >
          <span className="inline-flex items-center gap-2 border border-[#9c7a4d]/50 bg-[#9c7a4d]/10 text-[#c9a87c] px-4 py-1.5 text-[0.65rem] md:text-xs font-bold uppercase tracking-[0.22em] rounded-full">
            <span className="h-1.5 w-1.5 rotate-45 bg-[#c9a87c]" aria-hidden />
            {HERO.overline}
          </span>
        </motion.div>

        <h1 className="font-serif text-white leading-[0.98] text-4xl sm:text-6xl lg:text-7xl xl:text-8xl uppercase max-w-5xl">
          {HERO.lines.map((line, i) => (
            <Reveal key={i} load delay={0.35 + i * 0.14} as="span">
              {line}
            </Reveal>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-7 max-w-2xl text-[#cbd5e1] text-base md:text-lg leading-relaxed font-light"
        >
          {HERO.intro}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-9 flex flex-wrap gap-4"
        >
          <a
            href="#enquire"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 bg-[#9c7a4d] text-white hover:bg-[#b8935c] px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300"
          >
            Get Obligation-Free Quote
            <ArrowDown className="h-4 w-4 group-hover:translate-y-0.5 transition-transform" />
          </a>
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            onClick={() => trackCall("hero")}
            data-testid="hero-call-cta"
            className="inline-flex items-center gap-3 border border-white/25 text-white hover:border-[#c9a87c] hover:text-[#c9a87c] px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300"
          >
            <Phone className="h-4 w-4" /> Call {CONTACT.phone}
          </a>
        </motion.div>

        {/* Trust strip — instant-scan USPs for ad traffic */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          data-testid="hero-trust-strip"
          className="mt-12 pt-6 border-t border-white/15 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3"
        >
          {TRUST_POINTS.map((t) => (
            <span key={t} className="flex items-center gap-2.5 text-white/75 text-[0.7rem] md:text-xs font-semibold uppercase tracking-[0.16em]">
              <span className="h-1.5 w-1.5 rotate-45 bg-[#c9a87c] shrink-0" aria-hidden />
              {t}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
